import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  BondExchangeLanding,
  type BondProduct,
} from "@/components/BondExchangeLanding";
import { useAuth } from "@/auth/AuthProvider";
import { BaseListView } from "@/base";
import { bondSubmissionsListView } from "@/pages/bondSubmissionsListView";

// Construction Bond — contract surety (bid, performance & payment, maintenance).
// The "Start submission" CTA launches the Construction bond submission flow.
//
// BRK-S174 made this a FRONT DOOR rather than a poster. A broker landing here
// most often wants to know where their requests stand, and until now the answer
// lived a click away on `/surety/submissions` with no filters at all. The list
// is the same `bondSubmissionsListView` that page renders (BRK-S253), embedded
// under this page's heading — one fetch, one set of filters, one empty state —
// so the two surfaces cannot drift.
const PRODUCTS: BondProduct[] = [
  // $1M is the FICO Express single-bond limit at EVERY tier in the Guidelines
  // & Rate Manual. This read "under $500K" until BRK-S117 — understating the
  // filed program on the page a broker decides from.
  {
    name: "Bid Bonds",
    blurb: "Instant issue $1 million and under.",
    status: "active",
  },
  {
    name: "Performance & Payment (Contract)",
    blurb: "Requires an approved program",
    status: "active",
  },
  {
    name: "Maintenance & Warranty",
    blurb: "Post-completion coverage",
    status: "coming_soon",
  },
];

export function ConstructionBondPage() {
  const { authedRequest } = useAuth();
  // `?accountId=` — arriving from an insured's Actions menu (BRK-S449). The
  // landing carries it into the flow it starts, as the wizard's own `?account=`
  // (BRK-S160), so the broker does not pick the same insured twice. The same
  // key narrows the request list below to that insured's orders (BRK-S140).
  const [searchParams] = useSearchParams();
  const accountId = searchParams.get("accountId") ?? "";
  const startTo = accountId
    ? `/surety/submissions/new?${new URLSearchParams({ account: accountId }).toString()}`
    : "/surety/submissions/new";
  const requests = useMemo(
    () =>
      bondSubmissionsListView({ request: authedRequest, emptyAction: false }),
    [authedRequest],
  );
  return (
    <BondExchangeLanding
      eyebrow="Construction Bond"
      title="Get a bond quoted the same day"
      subtitle="Open a program once, then write bonds against approved capacity."
      cta={{ label: "Start submission", to: startTo }}
      // Present and plainly unavailable. Sending the application to the insured
      // is its own epic; the note says what it is and NOT when, because a date
      // nobody committed to is the same fake as a working-looking button.
      secondaryCta={{
        label: "Send Application to My Client",
        note: "Not available yet — start the submission yourself for now.",
      }}
      sectionTitle="Construction bond products"
      products={PRODUCTS}
    >
      <section aria-labelledby="bond-requests" className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2
            id="bond-requests"
            className="text-lg font-semibold text-brk-text"
          >
            Your bond requests
          </h2>
          {/* The full-page route of the same list — for a broker who wants it
              on its own, and the place `?accountId=` narrowing lives. */}
          <Link
            to="/surety/submissions"
            className="text-sm text-brk-accent hover:underline"
          >
            Open as a page →
          </Link>
        </div>
        {/* The same list as /surety/submissions (BRK-S253), under this
            section's heading. The landing already leads with "Start
            submission", so the empty state does not offer it a second time. */}
        <BaseListView def={requests} embedded />
      </section>
    </BondExchangeLanding>
  );
}
